import type { SimpleChanges } from '@angular/core';
import { Component, inject, Input, output, input, ChangeDetectionStrategy } from '@angular/core';

import {
  GuardedFeature,
  FeatureGuardMode,
  FeatureGuardDropdownItemComponent,
} from '@core/feature-guard-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  DropdownWidth,
  HeadingComponent,
  IconComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
  DropdownComponent,
} from '@core/user-interface-legacy';
import type { Visibility } from '@core/visibility-types';
import { VisibilityLevel } from '@core/visibility-types';

import type { VisibilitySelectLabels } from '../types';

export type VisibilityInfoContext = 'view' | 'access';

export interface VisibilityLabels {
  protected: string;
  public: string;
  private: string;
  subheader: {
    public: string;
    protected: string;
    private: string;
  };
}

@Component({
  selector: 'rt-visibility-selector-dropdown',
  styleUrls: ['visibility-selector-dropdown.component.less'],
  templateUrl: 'visibility-selector-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconComponent,
    HeadingComponent,
    FeatureGuardDropdownItemComponent,
    TranslatePipe,
  ],
})
export class VisibilitySelectorDropdownComponent {
  private readonly i18n = inject(I18nService);

  public readonly DropdownWidth = DropdownWidth;
  public readonly VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;

  public readonly visibility = input<Visibility>(undefined!);
  public readonly hasOnlyMeLevel = input<boolean>(true);
  public readonly hasSelectTeamMembers = input<boolean>(true);
  public readonly infoContext = input<VisibilityInfoContext>(undefined!);
  public readonly dropdownContentsOnly = input(false);
  @Input() public labels: VisibilitySelectLabels['dropdown'] = this.getLabels();

  public changeVisibilityLevel = output<VisibilityLevel>();
  public toggleDropdown = output<void>();

  public FeatureGuardMode: typeof FeatureGuardMode = FeatureGuardMode;
  public GuardedFeature: typeof GuardedFeature = GuardedFeature;

  public ngOnChanges(changes: SimpleChanges) {
    const infoContext = this.infoContext();
    if (changes.infoContext && infoContext && !this.labels) {
      this.labels = this.getLabels(infoContext);
    }
  }

  public ngOnInit() {
    if (!this.labels) {
      this.labels = this.getLabels(this.infoContext());
    }
  }

  private getLabels(context?: VisibilityInfoContext) {
    switch (context) {
      case 'access':
        return {
          header: {
            protected: this.i18n.translate('app.visibility.access_by'),
            public: this.i18n.translate('app.visibility.access_by_everyone'),
            private: this.i18n.translate('app.visibility.access_by_me'),
          },
          subheader: {
            public: this.i18n.translate('app.visibility.all_team_members_access'),
            protected: this.i18n.translate('app.visibility.selected_team_members_access'),
            private: this.i18n.translate('app.visibility.only_you_access'),
          },
        };

      default:
      case 'view':
        return {
          header: {
            public: this.i18n.translate('app.visibility.selector.public.heading'),
            protected: this.i18n.translate('app.visibility.selector.protected.heading'),
            private: this.i18n.translate('app.visibility.selector.private.heading'),
          },
          subheader: {
            public: this.i18n.translate('app.visibility.selector.public.subheading'),
            protected: this.i18n.translate('app.visibility.selector.protected.subheading'),
            private: this.i18n.translate('app.visibility.selector.private.subheading'),
          },
        };
    }
  }
}
